import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Slide {
  movieId: string;
  label: string;
  title: string;
  tagline: string;
  meta: string;
  synopsis: string;
  image: string;
}

const slides: Slide[] = [
  {
    movieId: 'm1',
    label: '本周焦点',
    title: '星海回声',
    tagline: '四光年之外，他只想听见一次潮汐',
    meta: '科幻 · 冒险 · 2025 · 128分钟',
    synopsis:
      '一次失败的深空探测任务，让信号工程师被困在空间站里。他每天收到的唯一声音，来自家乡海岸线的潮汐回声。',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20landscape%20of%20a%20misty%20green%20mountain%20lake%20at%20dawn%20with%20a%20tiny%20lone%20boat%2C%20a%20giant%20faint%20ringed%20planet%20in%20the%20sky%2C%20moody%20teal%20and%20warm%20amber%20tones%2C%20soft%20fog%2C%20minimal%20composition%2C%20cinematic%20lighting%2C%20high%20detail&width=1600&height=900&seq=qa-hero-m1&orientation=landscape',
  },
  {
    movieId: 't1',
    label: '口碑剧集',
    title: '长夜将明',
    tagline: '有些真相，只能在黎明前说完',
    meta: '悬疑 · 犯罪 · 2025 · 全36集',
    synopsis:
      '一桩跨越十二年的连环案，把所有线索都指向当年负责侦办的警队。新任刑警队长在重启调查时，发现师父可能是唯一知道全部真相的人。',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20landscape%20of%20a%20rainy%20city%20skyline%20at%20dawn%20with%20thick%20fog%20between%20tall%20towers%2C%20one%20single%20warm%20lit%20window%2C%20reflective%20wet%20rooftops%2C%20moody%20teal%20and%20amber%20tones%2C%20minimal%20composition%2C%20cinematic%20lighting&width=1600&height=900&seq=qa-hero-t1&orientation=landscape',
  },
  {
    movieId: 't6',
    label: '治愈推荐',
    title: '海风旅店',
    tagline: '四个房间，收留所有说不出口的心事',
    meta: '剧情 · 治愈 · 2025 · 全12集',
    synopsis:
      '海边小镇上有一家只有四个房间的旅店，老板娘从不问客人从哪里来。每一集，一位旅人带着自己的难题住进来。',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20landscape%20of%20a%20quiet%20seaside%20guesthouse%20at%20golden%20hour%20with%20white%20billowing%20curtains%2C%20a%20bicycle%20by%20the%20wall%20and%20a%20calm%20ocean%20horizon%2C%20soft%20teal%20water%20and%20warm%20amber%20sunlight%2C%20minimal%20composition%2C%20cinematic%20lighting&width=1600&height=900&seq=qa-hero-t6&orientation=landscape',
  },
];

export default function HeroCarousel() {
  const [active, setActive] = useState(0);

  const goTo = useCallback((index: number) => {
    setActive((index + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, []);

  const slide = slides[active];

  return (
    <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden bg-foreground-950">
      {slides.map((item, index) => (
        <div
          key={item.movieId}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden={index !== active}
        >
          <img
            src={item.image}
            alt={`${item.title}焦点图`}
            title={`${item.title} - 青苹果影院焦点推荐`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/65 to-foreground-950/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/30" />

      <div className="relative z-10 h-full w-full">
        <div className="h-full max-w-[1440px] mx-auto px-4 md:px-8 flex items-end md:items-center pb-16 md:pb-0">
          <div key={slide.movieId} className="max-w-2xl animate-fade-up">
            <div className="flex items-center gap-2.5">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              <span className="text-xs md:text-sm tracking-[0.22em] text-accent-300 font-label uppercase">
                {slide.label}
              </span>
            </div>

            <h1 className="mt-4 text-4xl md:text-6xl font-semibold text-background-50 leading-[1.1] tracking-tight">
              {slide.title}
            </h1>
            <p className="mt-3 md:mt-4 text-base md:text-xl text-background-50/85 font-heading">
              {slide.tagline}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs md:text-sm text-background-100/70">
              {slide.meta.split(' · ').map((item, index, list) => (
                <span key={item} className="flex items-center gap-3">
                  {item}
                  {index < list.length - 1 && (
                    <span className="w-1 h-1 rounded-full bg-background-100/40" aria-hidden="true" />
                  )}
                </span>
              ))}
            </div>

            <p className="mt-4 text-sm md:text-base text-background-100/75 leading-relaxed max-w-xl line-clamp-3">
              {slide.synopsis}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slide.movieId}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg" aria-hidden="true" />
                查看影片详情
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-50/35 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap"
              >
                浏览全部影片
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-5 left-0 right-0 px-4 md:px-8">
        <div className="max-w-[1440px] mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-2" role="tablist" aria-label="焦点图切换">
          {slides.map((item, index) => (
            <button
              key={item.movieId}
              type="button"
              role="tab"
              aria-selected={index === active}
              aria-label={`切换到 ${item.title}`}
              onClick={() => goTo(index)}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                index === active ? 'w-8 bg-accent-500' : 'w-4 bg-background-50/40 hover:bg-background-50/70'
              }`}
            />
          ))}
          <span className="ml-2 text-[11px] text-background-100/60 font-label tracking-widest">
            0{active + 1} / 0{slides.length}
          </span>
        </div>

        <div className="hidden sm:flex items-center gap-2">
          <button
            type="button"
            onClick={() => goTo(active - 1)}
            aria-label="上一张"
            className="w-9 h-9 rounded-full border border-background-50/30 text-background-50 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => goTo(active + 1)}
            aria-label="下一张"
            className="w-9 h-9 rounded-full border border-background-50/30 text-background-50 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
          </button>
        </div>
        </div>
      </div>
    </section>
  );
}